import { useState } from 'react'
import { Box, Container, Typography, Chip, Paper } from '@mui/material'
import { LocalOffer, CheckCircle, Lock, ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'
import AuthModal from '../auth/AuthModal'

const features = [
  '150 AI credits per month',
  'Full commercial-use license',
  'Create custom prompts for your images',
  'Unlimited downloads',
  'Priority support',
]

const PricingSection = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [authModalOpen, setAuthModalOpen] = useState(false)

  const handleStartPlan = () => {
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      setAuthModalOpen(true)
    }
  }

  return (
    <Box
      component="section"
      sx={{
        py: 6,
        position: 'relative',
        overflow: 'hidden',
        background: designSpecs.gradients.background,
      }}
    >
      {/* Background decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '25%',
          width: 384,
          height: 384,
          bgcolor: 'primary.main',
          opacity: 0.05,
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: '25%',
          width: 384,
          height: 384,
          bgcolor: 'secondary.main',
          opacity: 0.05,
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ mb: 3, color: 'grey.900' }}>
            Simple Pricing
          </Typography>
          <Typography variant="h4" sx={{ color: 'grey.600', fontWeight: 400 }}>
            Everything you need to transform your products into stunning visuals
          </Typography>
        </Box>

        {/* Pricing Card */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: designSpecs.borderRadius['2xl'],
            p: 5,
            border: '2px solid',
            borderColor: 'primary.main',
            borderOpacity: 0.1,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'white',
          }}
        >
          {/* Decorative gradient blob */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 160,
              height: 160,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Icon and Price */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  background: designSpecs.gradients.primary,
                  borderRadius: designSpecs.borderRadius.xl,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: 3,
                }}
              >
                <Box component="span" sx={{ fontSize: '3rem' }}>
                  👑
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: designSpecs.typography.sizes['6xl'],
                    fontWeight: designSpecs.typography.fontWeights.extrabold,
                    background: designSpecs.gradients.primary,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  $50
                </Typography>
              </Box>

              <Typography variant="h4" sx={{ color: 'grey.600', fontWeight: 500, mb: 1.5 }}>
                per month
              </Typography>

              <Chip
                icon={<CheckCircle sx={{ fontSize: '0.875rem !important' }} />}
                label="Best Value"
                sx={{
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  fontWeight: 600,
                  fontSize: designSpecs.typography.sizes.sm,
                }}
              />
            </Box>

            {/* Features List */}
            <Box sx={{ mb: 5 }}>
              {features.map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'primary.main',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: designSpecs.iconSizes.xs, color: 'white' }} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      ml: 2,
                      color: 'grey.700',
                      fontWeight: 500,
                      fontSize: designSpecs.typography.sizes.lg,
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleStartPlan}
              sx={{
                background: designSpecs.gradients.primary,
                py: 2.5,
                fontSize: designSpecs.typography.sizes.xl,
                fontWeight: 700,
                mb: 3,
                boxShadow: '0 20px 25px -5px rgba(99, 102, 241, 0.4)',
                '&:hover': {
                  background: designSpecs.gradients.primary,
                  boxShadow: '0 25px 50px -12px rgba(99, 102, 241, 0.6)',
                  transform: 'translateY(-4px)',
                },
              }}
              endIcon={<ArrowForward />}
            >
              Start Your Plan
            </Button>

            {/* Security and Info */}
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Lock sx={{ color: 'success.dark', fontSize: designSpecs.iconSizes.md }} />
                <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 500 }}>
                  Secure checkout • Instant access
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'grey.500', fontSize: designSpecs.typography.sizes.sm }}>
                No hidden fees. Cancel anytime.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} defaultMode="signup" />
    </Box>
  )
}

export default PricingSection
